/**
 * Maps data read from a Romanian eID card onto the client form fields.
 *
 * Kept pure and separate from the page so the mapping is unit-testable: the card
 * supplies several fields (ID series/number, issuer, issue and expiry dates) that
 * the form has inputs for but which were previously left blank after a read.
 */

export interface IdCardReadResult {
    cnp?: string;
    lastName?: string;
    firstName?: string;
    documentNumber?: string;
    issuedBy?: string;
    validFrom?: string;
    validUntil?: string;
    county?: string;
    city?: string;
    street?: string;
    streetNumber?: string;
    apartment?: string;
    postalCode?: string;
}

export interface ClientFormValues {
    cnp: string;
    nume: string;
    serieCI: string;
    nrCI: string;
    emitentCI: string;
    dataEliberareCI: string;
    dataExpirareCI: string;
    jud: string;
    oras: string;
    str: string;
    nr: string;
    ap: string;
    codPostal: string;
}

/**
 * Splits a Romanian ID document number such as `XX1234567` into its letter series
 * (`XX`) and numeric part (`1234567`). Returns the whole value as the number when
 * it does not follow that shape, so nothing is silently dropped.
 */
export function splitDocumentNumber(documentNumber?: string): { series: string; number: string } {
    const value = documentNumber?.trim() ?? '';
    if (value.length === 0) {
        return { series: '', number: '' };
    }
    const match = /^([A-Za-zĂÂÎŞŢăâîșşțţ]+)[\s-]*(\d+)$/.exec(value);
    if (match === null) {
        return { series: '', number: value };
    }
    return { series: match[1].toUpperCase(), number: match[2] };
}

/** Builds the full name as the form expects it (surname first). */
export function buildFullName(lastName?: string, firstName?: string): string {
    return [lastName, firstName]
        .map(part => part?.trim() ?? '')
        .filter(part => part.length > 0)
        .join(' ');
}

/**
 * Produces the complete set of client-form values for a card read. Every field is
 * always present so a second read cannot leave stale values from a previous card
 * behind — fields the card cannot supply are explicitly blanked.
 */
export function mapIdCardToClientForm(data: IdCardReadResult): ClientFormValues {
    const { series, number } = splitDocumentNumber(data.documentNumber);
    return {
        cnp: data.cnp ?? '',
        nume: buildFullName(data.lastName, data.firstName),
        serieCI: series,
        nrCI: number,
        emitentCI: data.issuedBy ?? '',
        dataEliberareCI: data.validFrom ?? '',
        dataExpirareCI: data.validUntil ?? '',
        jud: data.county ?? '',
        oras: data.city ?? '',
        str: data.street ?? '',
        nr: data.streetNumber ?? '',
        ap: data.apartment ?? '',
        codPostal: data.postalCode ?? '',
    };
}
